import React, { useEffect, useMemo, useState } from 'react';
import { DisplayInactiveToggle, FilterGrid, FilterField, type SearchFieldProps } from '@/components/ui';
import FiltersCard from '../../../components/FiltersCard';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { vendorService } from '../../../services/vendorService';
import { areasService } from '../../../services/areasService';
import { PO_TYPE_OPTIONS, PoStatus, PoType } from '../../../services/purchaseOrderService';

export interface PurchaseOrderFilterState {
  vendorIds: number[];
  types: string[];
  serviceAreaIds: number[];
  foremanAreaIds: number[];
  /**
   * A purchase order is simply active or inactive (V2-075, INACT-01), so a Display Inactive
   * toggle replaces the Status dropdown. Off (the default) hides inactive orders.
   */
  displayInactive: boolean;
}

/** The defaults: no vendor / type / area filter, inactive orders hidden. Clear All returns here. */
export const EMPTY_PO_FILTERS: PurchaseOrderFilterState = {
  vendorIds: [],
  types: [],
  serviceAreaIds: [],
  foremanAreaIds: [],
  displayInactive: false,
};

/**
 * The statuses sent for Display Inactive: off lists Reserved and Active orders (everything but
 * Inactive); on sends no status filter.
 */
export const poStatusesFor = (displayInactive: boolean): PoStatus[] | undefined =>
  displayInactive ? undefined : ['RESERVED', 'ACTIVE'];

/**
 * Every filter that hides purchase orders, the default Display Inactive off included
 * (V2-076, FLT-05), for the collapsed bar's "n filters applied".
 */
export const countActivePoFilters = (f: PurchaseOrderFilterState): number =>
  [f.vendorIds, f.types, f.serviceAreaIds, f.foremanAreaIds].filter((v) => v.length > 0).length +
  (f.displayInactive ? 0 : 1);

const sameList = (a: Array<string | number>, b: Array<string | number>) =>
  a.length === b.length && [...a].map(String).sort().join('|') === [...b].map(String).sort().join('|');

const filtersEqual = (a: PurchaseOrderFilterState, b: PurchaseOrderFilterState) =>
  sameList(a.vendorIds, b.vendorIds) &&
  sameList(a.types, b.types) &&
  sameList(a.serviceAreaIds, b.serviceAreaIds) &&
  sameList(a.foremanAreaIds, b.foremanAreaIds) &&
  a.displayInactive === b.displayInactive;

/** True when the filters differ from the defaults (amber chip, Clear All enabled). */
export const poFiltersChanged = (f: PurchaseOrderFilterState): boolean => !filtersEqual(f, EMPTY_PO_FILTERS);

interface Props {
  /** The APPLIED filters (what the list was fetched with). */
  filters: PurchaseOrderFilterState;
  /** Apply: called with the pending filters. */
  onFilterChange: (filters: PurchaseOrderFilterState) => void;
  onClearFilters: () => void;
  /** The page's search, shown in the panel's header bar (V2-077). */
  search?: SearchFieldProps;
  /** True when the search is applied: counts toward "differs from the defaults". */
  searchApplied?: boolean;
}

/**
 * Searchable multi-select filters, matching the convention in AllTasksFilters and
 * TaskFilters. Vendor leads and is the widest: HE-P03 records Gord identifying vendor as the
 * primary lookup in practice, and it is also the longest list, so the in-dropdown search
 * earns the most there.
 *
 * Pending vs applied (W-M1): the controls edit a pending copy and nothing is fetched until
 * Apply; Clear All resets and applies at once. Every list here is sent as a list - the server
 * parses them with parseIntListParam / parseEnumList
 * (server/src/modules/purchase-order/purchase-order.controller.ts).
 */
const PurchaseOrdersFilters: React.FC<Props> = ({ filters, onFilterChange, onClearFilters, search, searchApplied = false }) => {
  const [vendors, setVendors] = useState<Array<{ id: number; name: string }>>([]);
  const [serviceAreas, setServiceAreas] = useState<Array<{ id: number; name: string }>>([]);
  const [foremanAreas, setForemanAreas] = useState<
    Array<{ id: number; name: string; serviceAreaId?: number }>
  >([]);
  const [pending, setPending] = useState<PurchaseOrderFilterState>(filters);

  // Applied filters changed from outside (Clear All): mirror them into the controls.
  useEffect(() => {
    setPending(filters);
  }, [filters]);

  useEffect(() => {
    vendorService.getAllVendors().then(setVendors).catch(() => setVendors([]));
    areasService
      .getServiceAreas()
      .then((areas: any[]) => setServiceAreas(areas ?? []))
      .catch(() => setServiceAreas([]));
    areasService
      .getForemanAreas()
      .then((areas: any[]) => setForemanAreas(areas ?? []))
      .catch(() => setForemanAreas([]));
  }, []);

  const set = <K extends keyof PurchaseOrderFilterState>(
    field: K,
    value: PurchaseOrderFilterState[K]
  ) => {
    setPending((prev) => {
      const next = { ...prev, [field]: value };
      // Narrowing the service areas invalidates any foreman area chosen under one that is no
      // longer selected - leaving it would filter to nothing with no visible reason.
      if (field === 'serviceAreaIds') {
        const allowed = new Set(
          foremanAreas
            .filter(
              (fa) =>
                !(value as number[]).length ||
                (fa.serviceAreaId !== undefined && (value as number[]).includes(fa.serviceAreaId))
            )
            .map((fa) => fa.id)
        );
        next.foremanAreaIds = prev.foremanAreaIds.filter((id) => allowed.has(id));
      }
      return next;
    });
  };

  const visibleForemanAreas = useMemo(
    () =>
      pending.serviceAreaIds.length
        ? foremanAreas.filter(
            (fa) => fa.serviceAreaId !== undefined && pending.serviceAreaIds.includes(fa.serviceAreaId)
          )
        : foremanAreas,
    [foremanAreas, pending.serviceAreaIds]
  );

  const appliedFilterCount = countActivePoFilters(filters);
  const hasUnappliedChanges = !filtersEqual(pending, filters);

  const handleClear = () => {
    setPending(EMPTY_PO_FILTERS);
    onClearFilters();
  };

  return (
    <FiltersCard
      title="Purchase Order Filters"
      subtitle="Filter by vendor, type, and area"
      collapsible
      defaultCollapsed={false}
      appliedFilterCount={appliedFilterCount}
      hasActiveFilters={poFiltersChanged(filters) || searchApplied}
      search={search}
      onApply={() => onFilterChange(pending)}
      applyDisabled={!hasUnappliedChanges}
      onClear={handleClear}
    >
      <FilterGrid>
        <FilterField span={2}>
          <MultiSelectDropdown
            options={vendors}
            value={pending.vendorIds}
            onChange={(v) => set('vendorIds', v.map(Number))}
            getValue={(o) => o.id}
            formatOption={(o) => o.name}
            placeholder="All Vendors"
            label="Vendor"
            className="tw-w-full"
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown
            options={PO_TYPE_OPTIONS}
            value={pending.types}
            onChange={(v) => set('types', v.map(String))}
            getValue={(o) => o.value}
            formatOption={(o) => o.label}
            placeholder="All Types"
            label="Type"
            className="tw-w-full"
            // The six types are a fixed, meaningful order from the source sheet.
            preserveOptionsOrder
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown
            options={serviceAreas}
            value={pending.serviceAreaIds}
            onChange={(v) => set('serviceAreaIds', v.map(Number))}
            getValue={(o) => o.id}
            formatOption={(o) => o.name}
            placeholder="All Service Areas"
            label="Service Area"
            className="tw-w-full"
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown
            options={visibleForemanAreas}
            value={pending.foremanAreaIds}
            onChange={(v) => set('foremanAreaIds', v.map(Number))}
            getValue={(o) => o.id}
            formatOption={(o) => o.name}
            placeholder="All Foreman Areas"
            label="Foreman Area"
            className="tw-w-full"
          />
        </FilterField>
        <FilterField>
          <DisplayInactiveToggle
            id="po-display-inactive"
            checked={pending.displayInactive}
            onChange={(checked) => set('displayInactive', checked)}
          />
        </FilterField>
      </FilterGrid>
    </FiltersCard>
  );
};

export type { PoStatus, PoType };
export default PurchaseOrdersFilters;
